/**
 * curl --location --request POST 'https://bpi.briteverify.com/api/public/v1/fullverify' \
 *   --header 'Content-Type: application/json' \
 *   --header 'Authorization: ApiKey: api-key-here' \
 *   --data-raw '{
 *       "phone": "135315"
 *  }'
 */

import { EMAIL_REGEX } from 'commons/constants';
import { logError } from './logger';
import getEnvValue from './getEnvValue';

// Extracts just the domain portion of an email for non-PII diagnostics.
// Never log the full address (which includes the local part) to Sentry.
const getEmailDomain = email =>
  typeof email === 'string' && email.includes('@')
    ? email.split('@')[1]
    : undefined;

const BriteverifyClient = () => {
  // Six forms call this same shared client with the same publicly-exposed API key, so
  // BriteVerify's account-level rate limit is shared across the whole
  // site's traffic. Cache results per normalized email.
  const emailValidationCache = new Map();

  const root = {
    apiEndPoint:
      getEnvValue('NEXT_PUBLIC_BRITEVERIFY_API_URL') ??
      getEnvValue('REACT_APP_BRITEVERIFY_API_URL'),
    apiKey:
      getEnvValue('NEXT_PUBLIC_BRITEVERIFY_API_KEY') ??
      getEnvValue('REACT_APP_BRITEVERIFY_API_KEY'),
    creditLookupDelay: 10,

    request: async ({ endPoint, method = 'POST', body }) => {
      try {
        const payload = {
          method,
          body: body ? JSON.stringify({ ...body }) : '',
          headers: {
            'Content-Type': 'application/json',
            Authorization:
              getEnvValue('NEXT_PUBLIC_BRITEVERIFY_API_KEY') ??
              getEnvValue('REACT_APP_BRITEVERIFY_API_KEY'),
          },
        };
        const response = await fetch(endPoint, payload);
        const result = await response.json();
        if (response.status === 200) {
          return result;
        }
        if (!response.ok) {
          // Note: emailLookup() treats any non-200/error response as
          // non-blocking (it lets the form proceed), so this is never a
          // critical failure for the user - just log it for visibility,
          // not as a fatal/critical error.
          // Pass a real Error so Sentry captures this call
          // site's stack trace instead of synthesizing one from inside the
          // logger's Sentry.withScope() wrapper.
          // Only log non-PII diagnostics - `body`/`result` can echo back the
          // user's email address, so log just the domain, not the full address.
          logError(
            new Error(`Britverify Error: HTTP ${response.status}`),
            { status: `${response.status}` },
            { errors: result?.errors, emailDomain: getEmailDomain(body?.email) }
          );
          // Flag every non-ok response as an apiError, so emailLookup()
          // treats all failure shapes the same: fail open and skip the cache.
          return { ...result, apiError: `HTTP ${response.status}` };
        }
        return result;
      } catch (e) {
        logError(e, 'Britverify Error', {
          emailDomain: getEmailDomain(body?.email),
        });
        return {
          apiError: e.message,
        };
      }
    },

    emailLookup: async email => {
      const normalizedEmail = String(email)
        .trim()
        .toLowerCase();

      if (!EMAIL_REGEX.test(normalizedEmail)) {
        return {
          valid: false,
          message: 'Email address is invalid.',
        };
      }

      if (emailValidationCache.has(normalizedEmail)) {
        return emailValidationCache.get(normalizedEmail);
      }

      const endPoint = `${root.apiEndPoint}fullverify`;
      const response = await root.request({
        endPoint,
        body: { email: normalizedEmail },
      });

      // It should not block the form if the API/lookup failed for any
      // reason - and we deliberately do NOT cache these,
      // since they aren't a real verdict from BriteVerify.
      if (response.apiError || response?.errors) {
        return {
          valid: true,
          message: null,
        };
      }

      let result = { valid: false };
      if (response?.email?.status === 'invalid') {
        result = {
          valid: false,
          message: response.email.error,
        };
      } else if (
        ['valid', 'accept_all', 'unknown'].includes(response?.email?.status)
      ) {
        result = {
          valid: true,
          message: null,
        };
      }

      emailValidationCache.set(normalizedEmail, result);
      return result;
    },
  };
  return root;
};

export default BriteverifyClient();
